import React, { useEffect, useRef, useState } from "react";
import { Volume2, Languages, Map as MapIcon, BookOpen, ExternalLink, Share2, Copy, Check, Loader2, Download } from "lucide-react";
import { LanguagePicker } from "@/components/site/LanguagePicker";
import { useTTS } from "@/context/TTSContext";
import { T } from "@/context/LanguageContext";
import { shareLink } from "@/lib/api";

const btn =
  "flex items-center gap-2 rounded-full border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy transition-all hover:border-oldglory-red hover:text-oldglory-red";

export function UtilityBar({ paragraphs, onMap, wikipedia, sharePath }) {
  const tts = useTTS();
  const [srcOpen, setSrcOpen] = useState(false);
  const srcRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null); // {short_url, qr_svg} | {error}
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (srcRef.current && !srcRef.current.contains(e.target)) setSrcOpen(false);
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const listen = () => {
    if (!tts) return;
    tts.setContent(paragraphs);
    tts.stop();
    setTimeout(() => tts.play(), 60);
  };

  const openShare = async () => {
    const next = !shareOpen;
    setShareOpen(next);
    if (next && !share && sharePath) {
      setShareBusy(true);
      try {
        const res = await shareLink({ path: sharePath });
        setShare(res.ok ? res : { error: res.message || "Sharing isn't available for this page yet." });
      } catch {
        setShare({ error: "Couldn't create a share link right now." });
      } finally { setShareBusy(false); }
    }
  };

  const copy = () => {
    if (!share?.short_url) return;
    navigator.clipboard?.writeText(share.short_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-wrap items-center gap-2.5" data-testid="utility-bar">
      {tts?.supported && (
        <button onClick={listen} className={btn} data-testid="utility-listen">
          <Volume2 className="h-4 w-4" /> <T>Listen</T>
        </button>
      )}
      <LanguagePicker
        showGroups
        trigger={
          <button className={btn} data-testid="utility-translate">
            <Languages className="h-4 w-4" /> <T>Translate</T> <span className="text-[11px] opacity-60">▾</span>
          </button>
        }
      />
      <button onClick={onMap} className={btn} data-testid="utility-map">
        <MapIcon className="h-4 w-4" /> <T>Map</T>
      </button>
      <div ref={srcRef} className="relative">
        <button onClick={() => setSrcOpen((o) => !o)} className={btn} data-testid="utility-sources">
          <BookOpen className="h-4 w-4" /> <T>Sources</T> <span className="text-[11px] opacity-60">▾</span>
        </button>
        {srcOpen && (
          <div className="absolute left-0 top-full z-[60] mt-2 w-64 overflow-hidden rounded-lg border border-border bg-white shadow-2xl" data-testid="sources-menu">
            {wikipedia?.available && (
              <a href={wikipedia.wikipedia_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-2.5 text-sm text-ink hover:bg-parchment">
                Wikipedia <ExternalLink className="h-3.5 w-3.5 opacity-60" />
              </a>
            )}
            <a href="https://www.census.gov/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-2.5 text-sm text-ink hover:bg-parchment">
              U.S. Census Bureau <ExternalLink className="h-3.5 w-3.5 opacity-60" />
            </a>
            <a href="https://www.usa.gov/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-2.5 text-sm text-ink hover:bg-parchment">
              USA.gov <ExternalLink className="h-3.5 w-3.5 opacity-60" />
            </a>
          </div>
        )}
      </div>

      {sharePath && (
        <div ref={shareRef} className="relative">
          <button onClick={openShare} className={btn} data-testid="utility-share">
            <Share2 className="h-4 w-4" /> <T>Share</T> <span className="text-[11px] opacity-60">▾</span>
          </button>
          {shareOpen && (
            <div className="absolute left-0 top-full z-[60] mt-2 w-72 overflow-hidden rounded-xl border border-border bg-white p-4 shadow-2xl" data-testid="share-menu">
              {shareBusy ? (
                <div className="flex items-center gap-2 text-sm text-ink"><Loader2 className="h-4 w-4 animate-spin" /> <T>Creating branded link…</T></div>
              ) : share?.error ? (
                <p className="text-sm text-ink/70" data-testid="share-error">{share.error}</p>
              ) : share?.short_url ? (
                <>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-navy/50"><T>Branded share link</T></div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <input readOnly value={share.short_url} className="w-full rounded-lg border border-border bg-parchment/60 px-2.5 py-1.5 text-sm text-ink" data-testid="share-url" />
                    <button onClick={copy} className="shrink-0 rounded-lg bg-navy px-2.5 py-1.5 text-white" data-testid="share-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
                  </div>
                  {share.qr_svg && (
                    <div className="mt-3 flex items-center gap-3">
                      <img src={share.qr_svg} alt="QR code" className="h-24 w-24 rounded-lg border border-border bg-white p-1" data-testid="share-qr" />
                      <div className="text-xs text-ink/70">
                        <T>Scan or print for community boards.</T>
                        <a href={share.qr_svg} target="_blank" rel="noreferrer" className="mt-1.5 flex items-center gap-1 font-semibold text-oldglory-red" data-testid="share-qr-download"><Download className="h-3.5 w-3.5" /> <T>Download QR (SVG)</T></a>
                      </div>
                    </div>
                  )}
                  <p className="mt-2 text-[10px] text-ink/40"><T>Clicks tracked via SpLink.pro</T></p>
                </>
              ) : (
                <p className="text-sm text-ink/70"><T>Share link unavailable.</T></p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
